import React, { useRef } from "react"
import styled from "styled-components"
import { Link } from "gatsby"

import useTracking from "hooks/useTracking"
import useProductTracking from "hooks/useProductTracking"
import { minWidth, maxWidth, when, eq } from "utils"
import { formatCurrency } from "utils/currency"

import Icon from "icons"
import CloudinaryImage from "components/cloudinary-image"
import CroppedImage from "components/cropped-image"
import ArticleIcon from "images/article-icon.svg"

export const Wrapper = styled.div`
  margin-top: 3rem;
  padding-right: 7.3rem;

  ${maxWidth.desktop`
    padding-left: ${({ isScrollable }) => (isScrollable ? "0" : "7.3rem")};
  `}

  ${maxWidth.tablet`
    padding: 0 4rem;
  `}

  ${maxWidth.mobile`
    padding: ${({ isScrollable }) => (isScrollable ? "0" : "0 0 2rem 2rem ")};
  `}
`

export const Items = styled.div`
  position: relative;
`

const ScrollWrapper = styled.span`
  ${minWidth.mobile`
    ${eq("itemType", "Product")`
      display: none;
    `}
  `}

  ${when("isScrollable")`
    ${minWidth.mobile`
      display: none;
      `}  
  `}
`

const ScrollLabel = styled.div`
  font-size: 1.6rem;
  line-height: 2.2rem;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${minWidth.hamburgerLarge`
    font-size: 2rem;
    line-height: 2.7rem;
  `}
`

const ScrollArrowLineMobile = styled(Icon)`
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  margin: 0 1.5rem 1.5rem 0;
  ${minWidth.mobile`
    margin:0;
    display: none;
  `};
`

const ScrollArrowLineDesktop = styled(Icon)`
  display: none;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${minWidth.mobile`
    display: block;
  `}
`

export const Scroll = ({ itemType, label, isScrollable }) => (
  <ScrollWrapper itemType={itemType} isScrollable={isScrollable}>
    <ScrollLabel>{label || "scroll"}</ScrollLabel>
    <ScrollArrowLineMobile viewBox="0 0 348 12" id="arrow-line-mobile" />
    <ScrollArrowLineDesktop viewBox="0 0 823 12" id="arrow-line-desktop" />
  </ScrollWrapper>
)

export const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  ${minWidth.hamburgerLarge`
    width: calc(100% + 7.3rem);
  `};
`

export const Grid = styled.div`
  display: -webkit-box;
  overflow-x: auto;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`

const ArrowIcon = styled(Icon)`
  position: absolute;
  top: 0;
  right: 3.5rem;
  transform: translateY(-2.5rem) rotate(180deg);
  display: none;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  cursor: pointer;
  outline: none;

  ${when("disabled")`
    color: ${({ theme }) => (theme.component?.isDarkBackground ? "#aaa" : "rgba(34, 34, 34, 0.2)")};
    pointer-events: none;
  `}

  ${minWidth.hamburgerLarge`
    display: block;
  `}

  ${eq("direction", "right")`
    right: 0;
    transform: translateY(-2.5rem);
    transform-origin: center;
  `}

  ${when("ismodal")`
    top: 50%;
    left: 0;
    color: #fff;

    ${when("disabled")`
      color: #aaa;
    `}

    ${eq("direction", "right")`
      left: unset;
      right: 0;
    `}
  `}

  ${when("isScrollable")`
    ${minWidth.tablet`
      margin-top: -10rem;
      right: 3.5rem;
      top: 0;
      padding:0;
      ${eq("direction", "right")`
      right: 0;
      transform: translateY(-2.5rem);
      transform-origin: center;
  `}

      `}
  `}
`

export const Arrow = (props) => <ArrowIcon id="arrow-shop" width={24} height={25} {...props} />

export const ProductCardWrapper = styled.div`
  margin: 2rem 1.8rem 2rem 0;
  display: flex;
`

const Card = styled.div`
  width: 20.2rem;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  :first-child {
    margin-left: 0;
  }

  :last-child {
    margin-right: 0;
  }

  ${minWidth.tablet`
    width: 26rem;
  `}
`

const ArticleCardContainer = styled.div`
  border-radius: 0;
  display: flex;
  flex-direction: column;
  :first-child {
    margin-left: 0;
  }

  :last-child {
    margin-right: 0;
  }

  position: relative;
  margin: 0 0.9rem;
  background: #ffffff;
  width: 24.7rem;
  height: 34.9rem;

  ${minWidth.mobile`
     width:25.3rem;
     height: 35.8rem;
  `}
`
const ArticleContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex: 1 0 auto;
  padding: 1.5rem;

  ${minWidth.mobile`
    padding: 2rem
  `}
`

const ProductImage = styled(CloudinaryImage)`
  padding-bottom: 100%;
  overflow: hidden;
  transition: transform 0.2s linear;

  &:hover {
    transform: scale(1.25);
  }
`

const ProductMeta = styled.div`
  margin: 1.8rem 0;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`

const ProductTitle = styled.div`
  font-size: 2.4rem;
  font-weight: 400;
  line-height: 3.24rem;
  color: #485a50;
`

const ProductDescription = styled.div`
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
`

const Text = styled.span`
  display: -webkit-box;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
`

const Stars = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  line-height: 1.872rem;
`

const Star = ({ rating, productId, idx }) => (
  <svg
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: "0.5rem" }}
  >
    <defs>
      <linearGradient id={`grad-${productId}-${idx}`}>
        <stop offset={`${rating * 100}%`} stopColor="#222" />
        <stop offset={`${rating * 100}%`} stopColor="#fff" />
      </linearGradient>
    </defs>
    <path
      d="M7.42322 1.76517L8.76265 4.81197C8.79507 4.88591 8.84802 4.95001 8.9158 4.99734C8.98357 5.04467 9.0636 5.07344 9.14724 5.08055L12.5757 5.36344C12.6666 5.37041 12.7534 5.40299 12.825 5.45705C12.8965 5.5111 12.9497 5.5842 12.9777 5.6671C13.0057 5.75 13.0074 5.83898 12.9824 5.92276C12.9574 6.00655 12.907 6.08138 12.8375 6.13779L10.2364 8.30347C10.1732 8.35648 10.1262 8.42484 10.1004 8.50129C10.0746 8.57775 10.071 8.65945 10.09 8.7377L10.8695 11.9595C10.8901 12.0437 10.8843 12.1317 10.8529 12.2128C10.8214 12.2938 10.7656 12.3642 10.6925 12.4152C10.6195 12.4662 10.5323 12.4955 10.4418 12.4995C10.3514 12.5035 10.2617 12.4821 10.184 12.4378L7.23756 10.7317C7.16597 10.6901 7.08379 10.6681 7 10.6681C6.91621 10.6681 6.83403 10.6901 6.76244 10.7317L3.81604 12.4378C3.7383 12.4821 3.64862 12.5035 3.55818 12.4995C3.46775 12.4955 3.38055 12.4662 3.30746 12.4152C3.23436 12.3642 3.17861 12.2938 3.14713 12.2128C3.11566 12.1317 3.10986 12.0437 3.13047 11.9595L3.91002 8.7377C3.92897 8.65945 3.92536 8.57775 3.89958 8.50129C3.87379 8.42484 3.82679 8.35648 3.76357 8.30347L1.16255 6.13779C1.09301 6.08138 1.04256 6.00655 1.0176 5.92276C0.992631 5.83898 0.994263 5.75 1.02229 5.6671C1.05031 5.5842 1.10347 5.5111 1.17503 5.45705C1.2466 5.40299 1.33335 5.37041 1.42432 5.36344L4.85276 5.08055C4.9364 5.07344 5.01643 5.04467 5.0842 4.99734C5.15198 4.95001 5.20493 4.88591 5.23735 4.81197L6.57678 1.76517C6.61255 1.6864 6.67168 1.61933 6.74687 1.57221C6.82207 1.5251 6.91005 1.5 7 1.5C7.08995 1.5 7.17793 1.5251 7.25313 1.57221C7.32832 1.61933 7.38745 1.6864 7.42322 1.76517Z"
      fill={`url(#grad-${productId}-${idx})`}
      stroke="#222"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Empty = styled.div`
  margin: 0.75rem 0;
  flex: 1 0 auto;
  font-size: 1.6rem;
  line-height: 2.1rem;
`

const ShopNowLink = styled.a`
  margin-top: 1rem;
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1.9rem;
  text-decoration: underline;
  text-transform: uppercase;
  color: ${({ theme }) =>
    theme.component
      ? theme.component.isDarkBackground
        ? theme.component.ctaInverted
        : theme.component.cta
      : theme.colors.text.primary};
`

export const ProductCard = ({ price, product }) => {
  if (!product) {
    return null
  }

  const { description, image, media, onlineStoreUrl, productId, review, title } = product

  const { trackProductClick } = useProductTracking()
  const ref1 = useRef()
  const ref2 = useRef()

  const item = {
    itemType: "Product",
    itemId: productId,
    shopifyProductVariant: { price, product: { title } },
  }
  trackProductClick({ elementRef: ref1, item })
  trackProductClick({ elementRef: ref2, item })

  const renderStars = () => {
    const stars = []
    let rating = review?.productScore || 1

    for (let i = 0; i < 5; i += 1) {
      stars.push(
        <Star
          key={i}
          rating={rating > 1 ? 1 : rating > 0 ? rating : 0}
          productId={productId}
          idx={i}
        />,
      )
      rating -= 1
    }

    return stars
  }

  return (
    <Card>
      <ImageLink
        ref={ref1}
        href={onlineStoreUrl}
        title={title}
        target="_blank"
        rel="noreferrer"
        tabIndex={-1}
      >
        <ProductImage
          baseUrl={image || media[0]?.image.originalSrc}
          images={[
            {
              screenSize: 768,
              transforms: "c_fill,g_auto,w_260,h_260,q_auto,f_auto,fl_lossy",
            },
            {
              transforms: "c_fill,g_auto,w_202,h_202,q_auto,f_auto,fl_lossy",
            },
          ]}
          lazy
        />
      </ImageLink>
      <ProductMeta>
        <a ref={ref2} href={onlineStoreUrl} title={title} target="_blank" rel="noreferrer">
          <ProductTitle>{title}</ProductTitle>
        </a>
        <ProductDescription>
          {description && <Text title={description}>{description}</Text>}
          {formatCurrency(price, true)}
        </ProductDescription>
        <Stars>
          {renderStars()} ({review?.totalReviews || 0})
        </Stars>
        <Empty />
      </ProductMeta>
    </Card>
  )
}

export const ImageLink = styled.a`
  overflow: hidden;
  width: 20.2rem;
  ${minWidth.tablet`
    width: 26rem;
  `}
`

export const ImageVideoWrapper = styled.div`
  position: relative;
  width: 32.2rem;
  margin-left: 1.8rem;
  cursor: pointer;
`

export const ExpandIcon = styled(Icon)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: #fff;
`

export const ImageRegular = styled(CloudinaryImage)`
  padding-bottom: calc(458 / 322 * 100%);
`

export const ImageCropped = styled(CroppedImage)`
  padding-bottom: calc(458 / 322 * 100%);
`

export const Video = styled.video`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const MobileVideo = styled(Video)`
  ${minWidth.mobile`
    display: none;
  `}
`

export const DesktopVideo = styled(Video)`
  display: none;

  ${minWidth.mobile`
    display: block;
    opacity: 0;
  `}
`

const ArticleImage = styled(CloudinaryImage)`
  width: 24.7rem;
  height: 17.4rem;

  ${minWidth.mobile`
    width: 25.3rem;
    height: 17.8rem;
  `};
`
export const ArticleCategory = styled.span`
  font-size: 1.2rem;
  line-height: 1.17rem;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${maxWidth.tablet`
    font-size: 1.2rem;
    margin: 0.8rem 0 0.8rem 0;
  `}

  ${maxWidth.mobile`
    font-size: 1.1rem;
    margin: 0.5rem 0 0.5rem 0;
  `}
`

const CollaboratorName = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.62rem;
  letter-spacing: 0.2rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  opacity: 0.6;
`

const ArticleTitleLink = styled(Link)`
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  margin-top: 0.5rem;
  font-size: 1.69rem;
  line-height: 2.23rem;
`

const Author = styled.div`
  margin-top: 0.5rem;
  font-size: 1.1rem;
  ${minWidth.mobile`
     margin-top: 1.8rem;
     font-size: 1.13rem;
     line-height: 1.62rem;
     letter-spacing: 0.1rem;
`}
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  opacity: 0.8;
`
export const IconWrapper = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 1.2rem;
  z-index: 2;
`

export const ArticleCard = ({ article = {}, pageType, tracking, trackingId }) => {
  if (!article?.id) {
    return null
  }
  const { id, category, subcategory, previewImage, titles, partner, sponsor, url, authors } =
    article

  const { trackRender, trackView, trackClick } = useTracking()
  const ref1 = useRef()
  const ref2 = useRef()

  const collaborator = partner || sponsor
  const trackId =
    trackingId || (partner ? "partner_article" : sponsor ? "sponsor_article" : `article-${id}`)
  const extraProps = {
    ...(pageType && { page_type: pageType }),
    click_title: titles.site || titles.body,
    click_url: `/articles/${url}`,
    article_id: id,
    ...(collaborator &&
      (partner ? { partner_id: collaborator.partnerId } : { sponsor_id: collaborator.sponsorId })),
    category: "Content Engagement",
    content_category: category,
    content_subcategory: subcategory,
  }

  if (tracking && tracking.includes("Render")) {
    trackRender(ref1, trackId, extraProps)
  }

  if (tracking && tracking.includes("View")) {
    trackView(ref1, trackId, extraProps)
  }

  if (tracking && tracking.includes("Click")) {
    trackClick(ref1, trackId, extraProps)
    trackClick(ref2, trackId, extraProps)
  }

  const author = authors && authors[0]

  return (
    <ArticleCardContainer>
      <Link ref={ref1} to={`/articles/${url}`} title={titles.site || titles.body} tabIndex={-1}>
        <ArticleImage
          baseUrl={JSON.parse(previewImage).path}
          images={[{ transforms: "c_fill,g_auto,w_254,h_173,q_auto,f_auto,fl_lossy" }]}
          lazy
        />
      </Link>
      <ArticleContent>
        {collaborator && (
          <CollaboratorName>
            {partner ? "paid content for" : "sponsored by"} {collaborator.name}
          </CollaboratorName>
        )}
        <ArticleCategory>{category}</ArticleCategory>

        <ArticleTitleLink
          ref={ref2}
          to={`/articles/${url}`}
          title={titles.site || titles.body}
          aria-label={titles.site || titles.body}
        >
          {titles.site || titles.body}
        </ArticleTitleLink>
        {author &&
          (author.url ? (
            <Author>
              <Link to={`/wc/${author.url}`} title={author.name} tabIndex={-1}>
                {author.name}
              </Link>
            </Author>
          ) : (
            <Author>{author.name}</Author>
          ))}
      </ArticleContent>
      <IconWrapper>
        <img src={ArticleIcon} alt="article-icon" />
      </IconWrapper>
    </ArticleCardContainer>
  )
}
